/* ==========================================================================
   Partner enquiry form
   --------------------------------------------------------------------------
   Drives the form, the modal, and the POST to Salesforce.

   THE CONTRACT
   The body is a JSON object whose keys are the members of
   DoctorLeadFormController.PartnerLeadRequest, spelled exactly as Apex spells
   them. Apex binds by property name, so a renamed key does not error - it
   writes null. The input `name` attributes in ta-lead-fields.liquid are the
   single source of those names; nothing here re-spells them.

   WHY THIS IS NOT WEB-TO-LEAD
   Web-to-Lead returns 200 for everything, including leads it silently drops,
   and it bypasses the controller's validation, its record type, and the
   'Partner Web Form' lead source the reporting depends on. This endpoint
   answers truthfully, so a failure can be shown to the visitor instead of a
   thank-you they should not have been given.

   THE FALLBACK IS THE POINT
   Any failure - endpoint not configured yet, CORS not allow-listed, org down,
   network gone - lands the visitor on the real Salesforce form rather than a
   dead end. Until the Apex is deployed to production this is the ONLY path,
   and the button still works.
   ========================================================================== */

(function () {
  'use strict';

  // currentProviders is a checkbox group, so it is gathered separately.
  var MULTI = 'currentProviders';

  /* --- Cloudflare Turnstile ------------------------------------------------
     Loaded only when a widget is on the page, which happens only once a site
     key is set in theme settings. No key, no script, no behaviour change.

     Explicit rendering rather than Turnstile's auto-scan: the modal is present
     on every page and the page form can be on screen too, so an auto-scan
     would mount two widgets and the second would collide with the first.

     Tokens are SINGLE USE and expire after about five minutes. Every failed
     submit therefore resets the widget - without that, a visitor who corrects
     a typo and tries again is refused by Cloudflare rather than by us, and the
     message they see makes no sense.                                        */
  var TURNSTILE_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
  var turnstileLoading = null;

  function loadTurnstile() {
    if (window.turnstile) return Promise.resolve();
    if (turnstileLoading) return turnstileLoading;

    turnstileLoading = new Promise(function (resolve, reject) {
      var s = document.createElement('script');
      s.src = TURNSTILE_SRC;
      s.async = true;
      s.defer = true;
      s.onload = function () { resolve(); };
      // Rejected, not swallowed: mountTurnstile leaves the form usable, and the
      // reason belongs in the console for whoever has to work out why.
      s.onerror = function () { reject(new Error('Turnstile script failed to load')); };
      document.head.appendChild(s);
    });
    return turnstileLoading;
  }

  function mountTurnstile(form) {
    var box = form.querySelector('[data-ta-lf-turnstile]');
    if (!box || box.dataset.mounted) return;

    loadTurnstile().then(function () {
      if (box.dataset.mounted) return;
      box.dataset.mounted = '1';
      box.dataset.widgetId = window.turnstile.render(box, {
        sitekey: box.dataset.sitekey,
        // A token that quietly expires would fail the next submit for no visible
        // reason, so take a fresh one rather than leaving a stale one in place.
        'expired-callback': function () { window.turnstile.reset(box.dataset.widgetId); },
        'error-callback': function () { return true; }
      });
    }).catch(function (err) {
      /* Cloudflare unreachable. The form stays usable and Salesforce decides:
         with verification on it refuses, with it off the enquiry goes through.
         Silently blocking the form here would lose enquiries to an outage that
         is not the visitor's fault. */
      console.warn('[ta-leadform] turnstile unavailable:', err && err.message);
    });
  }

  function turnstileToken(form) {
    var box = form.querySelector('[data-ta-lf-turnstile]');
    if (!box || !window.turnstile || !box.dataset.widgetId) return '';
    return window.turnstile.getResponse(box.dataset.widgetId) || '';
  }

  function resetTurnstile(form) {
    var box = form.querySelector('[data-ta-lf-turnstile]');
    if (!box || !window.turnstile || !box.dataset.widgetId) return;
    window.turnstile.reset(box.dataset.widgetId);
  }

  function cfg(el) {
    var root = el.closest('[data-ta-lf-root]');
    return {
      endpoint: (root && root.dataset.endpoint) || '',
      fallback: (root && root.dataset.fallback) || '',
      root: root
    };
  }

  /* --- validation --------------------------------------------------------
     Every rule here mirrors something that would otherwise fail downstream:
     a Salesforce length limit, a NANP rule, or an address that cannot receive
     mail. The server still decides - this exists so the visitor is told before
     the round trip, not instead of it.
     ---------------------------------------------------------------------- */

  // Salesforce Lead field lengths. Over these the insert fails outright, and
  // the visitor would see a generic error for a fixable typo.
  var MAXLEN = { firstName: 40, lastName: 80, email: 80, phone: 40, practiceName: 255 };

  // The domains people actually mistype, and the misspellings they produce.
  // Suggested, never enforced - "gmial.com" is almost certainly wrong, but it
  // is not this form's place to refuse an address someone insists on.
  var TYPOS = {
    'gmial.com': 'gmail.com', 'gmai.com': 'gmail.com', 'gmail.co': 'gmail.com',
    'gmaill.com': 'gmail.com', 'gnail.com': 'gmail.com', 'yahooo.com': 'yahoo.com',
    'yaho.com': 'yahoo.com', 'hotmial.com': 'hotmail.com', 'hotmai.com': 'hotmail.com',
    'outlok.com': 'outlook.com', 'iclould.com': 'icloud.com', 'icloud.co': 'icloud.com'
  };

  function fail(input, message) {
    input.setAttribute('aria-invalid', 'true');
    var box = document.getElementById(input.id + '-err');
    if (box) { box.textContent = message; box.hidden = false; }
  }

  function clear(input) {
    input.removeAttribute('aria-invalid');
    var box = document.getElementById(input.id + '-err');
    if (box) box.hidden = true;
  }

  /* --- email -------------------------------------------------------------- */

  function checkEmail(v) {
    // Deliberately not RFC 5322. That grammar accepts quoted strings and
    // comments no dental practice will ever type, and rejecting a real address
    // costs a lead - so this checks the shape that actually matters.
    var m = /^([^@\s]+)@([^@\s]+)$/.exec(v);
    if (!m) return 'Enter an email address, like name@practice.com';

    var local = m[1], domain = m[2];

    if (local.length > 64) return 'The part before the @ is too long.';
    if (/^\.|\.$|\.\./.test(local)) return 'That address has a misplaced dot.';
    if (/^\.|\.$|\.\.|^-|-$/.test(domain)) return 'That domain does not look right.';
    if (domain.indexOf('.') === -1) return 'The domain needs a dot, like practice.com';
    if (!/^[a-z0-9.-]+$/i.test(domain)) return 'That domain has characters an address cannot use.';

    var tld = domain.split('.').pop();
    if (tld.length < 2 || !/^[a-z]+$/i.test(tld)) return 'That address is missing a valid ending, like .com';

    return null;
  }

  function suggestEmail(v) {
    var at = v.lastIndexOf('@');
    if (at === -1) return null;
    var domain = v.slice(at + 1).toLowerCase();
    return TYPOS[domain] ? v.slice(0, at + 1) + TYPOS[domain] : null;
  }

  /* --- US phone ----------------------------------------------------------- */

  // Digits only, with a leading country code dropped. Everything a person might
  // type between the digits - dots, dashes, brackets, spaces, +1 - is noise.
  function digits(v) {
    var d = (v || '').replace(/\D/g, '');
    if (d.length === 11 && d.charAt(0) === '1') d = d.slice(1);
    return d;
  }

  /* North American Numbering Plan, which is what "US only" actually means:
       - ten digits
       - area code and exchange both start 2-9; 0 and 1 are reserved
       - neither may be N11 (211, 411, 911 and the rest are service codes)
       - 555-0100..0199 is the block reserved for fiction, and is the single
         most common thing typed into a phone box that is not a phone number */
  function checkPhone(v) {
    var d = digits(v);
    if (!d) return null; // optional field

    if (d.length !== 10) {
      return d.length < 10
        ? 'A US number needs 10 digits — this has ' + d.length + '.'
        : 'That is too long for a US number. Ten digits, please.';
    }

    var area = d.slice(0, 3), exch = d.slice(3, 6), line = d.slice(6);

    if (area.charAt(0) === '0' || area.charAt(0) === '1') return 'A US area code cannot start with ' + area.charAt(0) + '.';
    if (exch.charAt(0) === '0' || exch.charAt(0) === '1') return 'That is not a valid US number.';
    if (area.charAt(1) === '1' && area.charAt(2) === '1') return area + ' is a service code, not an area code.';
    if (exch.charAt(1) === '1' && exch.charAt(2) === '1') return 'That is not a valid US number.';
    if (exch === '555' && line.charAt(0) === '0' && parseInt(line, 10) <= 199) {
      return '555-01xx numbers are reserved for fiction — we need one we can call.';
    }
    if (/^(\d)\1{9}$/.test(d)) return 'That does not look like a real number.';

    return null;
  }

  function prettyPhone(v) {
    var d = digits(v);
    if (d.length !== 10) return v;
    return '(' + d.slice(0, 3) + ') ' + d.slice(3, 6) + '-' + d.slice(6);
  }

  /* --- the whole form ----------------------------------------------------- */

  function validate(form) {
    var ok = true;
    var first = null;

    function bad(el, msg) {
      fail(el, msg);
      ok = false;
      if (!first) first = el;
    }

    form.querySelectorAll('input[name], select[name]').forEach(function (el) {
      if (el.type === 'checkbox' || el.type === 'radio' || el.name === 'company_url') return;
      clear(el);

      var v = (el.value || '').trim();
      var cap = MAXLEN[el.name];

      if (el.hasAttribute('required') && !v) {
        bad(el, 'This one is needed.');
        return;
      }
      if (!v) return;

      if (cap && v.length > cap) {
        bad(el, 'That is longer than we can store — ' + cap + ' characters maximum.');
        return;
      }
      if (el.type === 'email') {
        var e = checkEmail(v);
        if (e) { bad(el, e); return; }
      }
      if (el.type === 'tel') {
        var p = checkPhone(v);
        if (p) { bad(el, p); return; }
      }
    });

    if (first) first.focus();
    return ok;
  }

  /* --- payload ------------------------------------------------------------ */

  function payload(form) {
    var out = {};
    var providers = [];

    form.querySelectorAll('input[name], select[name], textarea[name]').forEach(function (el) {
      var k = el.name;
      if (k === 'company_url') return; // honeypot never travels
      // Turnstile's own hidden input. Collected below under a name Apex can
      // bind; sent as-is it would break deserialization for every submission.
      if (k === 'cf-turnstile-response') return;

      if (k === MULTI) {
        if (el.checked) providers.push(el.value);
        return;
      }

      if (el.type === 'radio' && !el.checked) return;

      var v = (el.value || '').trim();
      if (!v) return; // omit empties so Apex leaves the field alone

      // One stored shape, whatever was typed. Salesforce does not normalise
      // phone numbers, so without this the org accumulates the same number in
      // four different formats and no two of them match on a search.
      out[k] = el.type === 'tel' ? prettyPhone(v) : v;
    });

    if (providers.length) out.currentProviders = providers;

    /* Sent under our own name, not Turnstile's `cf-turnstile-response`. A hyphen
       cannot be an Apex member name, and JSON.deserialize refuses a key it has
       nowhere to put - which would fail EVERY submission, not just bot ones.
       The widget's own hidden input is left out of the loop above for the same
       reason: it is collected here, deliberately, or not at all. */
    var token = turnstileToken(form);
    if (token) out.turnstileToken = token;

    return out;
  }

  /* --- submit ------------------------------------------------------------- */

  function submit(form) {
    var c = cfg(form);
    var button = form.querySelector('[data-ta-lf-submit]');
    var done = form.querySelector('[data-ta-lf-done]');
    var bad = form.querySelector('[data-ta-lf-fail]');
    var badText = form.querySelector('[data-ta-lf-fail-text]');
    var back = form.querySelector('[data-ta-lf-fallback]');
    var trap = form.querySelector('[name="company_url"]');

    if (!validate(form)) return;

    // Everything except the result panels, hidden together once there is a
    // result to show in their place.
    /* Everything that is not the result, hidden together.
       The lede sits OUTSIDE the form - it belongs to the section, not the
       fieldset - so it has to be reached through the root or it is left
       stranded above the thank-you as a line about a form that is gone. */
    function hideForm() {
      form.querySelectorAll('.ta-lf__set, .ta-lf__actions, .ta-lf__small').forEach(function (el) {
        el.hidden = true;
      });
      if (c.root) {
        c.root.querySelectorAll('.ta-lf__lede').forEach(function (el) { el.hidden = true; });
        // Lets the card drop its form-sized padding around a short result.
        c.root.classList.add('ta-lf--resolved');
      }
    }

    // A filled honeypot is a bot. Show the thank-you and send nothing, so the
    // bot cannot tell it was caught and retune.
    if (trap && trap.value) {
      hideForm();
      if (done) done.hidden = false;
      return;
    }

    function collapse(message) {
      hideForm();
      if (badText) badText.textContent = message;
      if (back && c.fallback) back.href = c.fallback;
      if (bad) bad.hidden = false;
    }

    if (!c.endpoint) {
      collapse('Our enquiry system is not connected yet. Use the full application form and we will pick it up from there.');
      return;
    }

    var label = button.textContent;
    button.disabled = true;
    button.textContent = 'Sending…';

    // 20s ceiling: a hung request must resolve into the fallback rather than
    // leaving the visitor on a spinner for ever.
    var stop = new AbortController();
    var timer = setTimeout(function () { stop.abort(); }, 20000);

    fetch(c.endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload(form)),
      signal: stop.signal
    })
      .then(function (r) {
        return r.text().then(function (body) {
          return { ok: r.ok, status: r.status, body: body };
        });
      })
      .then(function (res) {
        clearTimeout(timer);
        if (!res.ok) throw new Error('HTTP ' + res.status + ' ' + res.body.slice(0, 200));

        hideForm();
        if (done) done.hidden = false;

        // Announced to the page so a theme analytics block can pick it up
        // without this file knowing anything about analytics.
        document.dispatchEvent(new CustomEvent('ta:lead-created', { detail: { raw: res.body } }));
      })
      .catch(function (err) {
        clearTimeout(timer);
        button.disabled = false;
        button.textContent = label;
        // Logged, not shown: the visitor gets a route forward, the developer
        // gets the reason.
        console.warn('[ta-leadform] submit failed:', err && err.message);
        // The token was spent on that attempt. Without this a retry is refused
        // by Cloudflare for reasons the visitor cannot see or act on.
        resetTurnstile(form);
        collapse('We could not send that just now. Your details are safe — open the full application form and it will only take a moment.');
      });
  }

  function wire(form) {
    mountTurnstile(form);

    form.querySelectorAll('input, select').forEach(function (i) {
      i.addEventListener('input', function () { clear(i); });
    });

    // Formatted on blur, not on keystroke. Reformatting while someone is still
    // typing moves the caret and fights them; on leaving the field it is a
    // tidy-up they asked for by finishing.
    form.querySelectorAll('input[type="tel"]').forEach(function (i) {
      i.addEventListener('blur', function () {
        if (!i.value) return;
        var problem = checkPhone(i.value);
        if (problem) { fail(i, problem); return; }
        i.value = prettyPhone(i.value);
      });
    });

    // A likely typo is offered, never imposed - one click to accept it.
    form.querySelectorAll('input[type="email"]').forEach(function (i) {
      i.addEventListener('blur', function () {
        var v = (i.value || '').trim();
        if (!v || checkEmail(v)) return;
        var better = suggestEmail(v);
        if (!better) return;
        var box = document.getElementById(i.id + '-err');
        if (!box) return;
        box.textContent = '';
        var ask = document.createElement('button');
        ask.type = 'button';
        ask.className = 'ta-lf__suggest';
        ask.textContent = 'Did you mean ' + better + '?';
        ask.addEventListener('click', function () { i.value = better; clear(i); });
        box.appendChild(ask);
        box.hidden = false;
      });
    });

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      submit(form);
    });
  }

  /* --- modal -------------------------------------------------------------- */

  function modal() {
    var box = document.querySelector('[data-ta-lf-modal]');
    if (!box) return;

    var panel = box.querySelector('.ta-lf-modal__panel');
    var opener = null;

    function open(from) {
      opener = from || null;
      box.setAttribute('open', '');
      document.body.classList.add('ta-lf-locked');
      var first = panel.querySelector('input, select, button');
      if (first) first.focus();
    }

    function close() {
      box.removeAttribute('open');
      document.body.classList.remove('ta-lf-locked');
      if (opener && opener.focus) opener.focus();
    }

    box.querySelectorAll('[data-ta-lf-close]').forEach(function (b) {
      b.addEventListener('click', close);
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && box.hasAttribute('open')) close();
    });

    // Keeps tab inside the dialog. Without this, tabbing walks into the page
    // behind it, which for a screen reader means the dialog silently ends.
    panel.addEventListener('keydown', function (e) {
      if (e.key !== 'Tab') return;
      var able = panel.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled])');
      var live = Array.prototype.filter.call(able, function (el) { return el.offsetParent !== null; });
      if (!live.length) return;
      var first = live[0];
      var last = live[live.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    });

    /* Any link pointing at the configured trigger path opens the dialog
       instead of navigating - so every existing "Talk to our team" button on
       every section works without editing a single one of them.

       Below 750px the click is left alone and the visitor gets the real page.
       A nineteen-field form in a sheet on a phone is worse than a page, and
       the page is a genuine destination, not a degraded one. */
    var triggers = (box.dataset.trigger || '').split(',').map(function (s) { return s.trim(); }).filter(Boolean);
    if (!triggers.length) return;

    document.addEventListener('click', function (e) {
      var a = e.target.closest('a[href]');
      if (!a) return;
      if (window.matchMedia('(max-width: 749px)').matches) return;

      var href = a.getAttribute('href') || '';
      var hit = triggers.some(function (t) { return href === t || href.indexOf(t) === 0; });
      if (!hit) return;

      e.preventDefault();
      open(a);
    });
  }

  function boot() {
    document.querySelectorAll('[data-ta-lf-form]').forEach(wire);
    modal();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
